/**
 * WorkspaceMenuSection — Team workspaces (V2 additive).
 *
 * NOT a standalone control: this renders the "Workspace" SECTION inside the
 * header's existing user dropdown (between the user info block and Quick
 * Settings). Rows match the dropdown's item styling. Switching changes the
 * workspace context (workspaceStore) — every scoped API call then carries the
 * org id via the shared interceptor — and routes home so the new workspace's
 * content loads.
 *
 * The Create-team modal is owned by the PARENT (Header): this section unmounts
 * when the dropdown closes, so any modal opened from here must live above it.
 */
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Check, Plus, User as UserIcon, Users } from 'lucide-react';
import { useUserStore } from '@/store/userStore';
import { useWorkspaceStore } from '@/store/workspaceStore';

export function WorkspaceMenuSection({ onSwitched, onCreateTeam }: {
  /** Called after a workspace is picked (parent closes the dropdown). */
  onSwitched: () => void;
  /** Open the Create-team modal (owned by the parent, above this dropdown). */
  onCreateTeam: () => void;
}) {
  const { t } = useTranslation('v2');
  const navigate = useNavigate();
  const user = useUserStore((s) => s.user);
  const refreshUser = useUserStore((s) => s.refreshUser);
  const activeOrgId = useWorkspaceStore((s) => s.activeOrgId);
  const setActiveOrg = useWorkspaceStore((s) => s.setActiveOrg);

  const orgs = (user?.organizations || []).filter((o) => o.orgStatus !== 'expired');
  const activeOrg = orgs.find((o) => o.orgId === activeOrgId) || null;

  const switchTo = (orgId: string | null) => {
    onSwitched();
    if (orgId === activeOrgId) return;
    setActiveOrg(orgId);
    void refreshUser();
    navigate('/');
  };

  return (
    <div className="py-2 border-b border-glass-border">
      <p className="px-4 pt-1 pb-2 text-[10px] font-semibold uppercase tracking-wide text-text-tertiary">
        {t('team.workspace')}
      </p>

      <button
        onClick={() => switchTo(null)}
        className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-surface-light transition-colors"
      >
        <span className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center shrink-0">
          <UserIcon className="w-4 h-4 text-text-secondary" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm text-text-primary truncate">{t('team.personalWorkspace')}</span>
          <span className="block text-[10px] text-text-tertiary truncate">{t('team.personalHint')}</span>
        </span>
        {!activeOrg && <Check className="w-4 h-4 text-primary shrink-0" />}
      </button>

      {orgs.map((o) => (
        <button
          key={o.orgId}
          onClick={() => switchTo(o.orgId)}
          className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-surface-light transition-colors"
        >
          <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-primary/30 to-accent/30 flex items-center justify-center shrink-0">
            <Users className="w-4 h-4 text-primary-light" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm text-text-primary truncate">{o.name}</span>
            <span className="block text-[10px] text-text-tertiary truncate">
              {o.orgStatus === 'pending'
                ? t('team.pendingSetup')
                : t('team.switcherOrgHint', { credits: Math.floor(o.remainingCredit) })}
            </span>
          </span>
          {activeOrgId === o.orgId && <Check className="w-4 h-4 text-primary shrink-0" />}
        </button>
      ))}

      <button
        onClick={onCreateTeam}
        className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-surface-light transition-colors"
      >
        <span className="w-7 h-7 rounded-lg border border-dashed border-text-tertiary/50 flex items-center justify-center shrink-0">
          <Plus className="w-4 h-4 text-text-secondary" />
        </span>
        <span className="text-sm text-text-secondary">{t('team.createTeam')}</span>
      </button>
    </div>
  );
}
